import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Clé de cache du profil utilisateur (localStorage)
const KEY_TOKEN         = 'promail_token';
const KEY_REFRESH_TOKEN = 'promail_refresh_token';
const KEY_USER_EMAIL    = 'promail_user_email';
const KEY_USER_CACHE    = 'promail_user_cache'; // profil sérialisé pour le mode hors-ligne

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [token, setToken]     = useState(null);
  const [loading, setLoading] = useState(true);
  // Évite les rafraîchissements simultanés (multiples requêtes 401 en même temps)
  const isRefreshing      = useRef(false);
  const refreshSubscribers = useRef([]);

  // ── Intercepteur axios : refresh token automatique sur 401 ─────────────
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        const isAuthEndpoint =
          originalRequest.url?.includes('/auth/refresh') ||
          originalRequest.url?.includes('/auth/login')  ||
          originalRequest.url?.includes('/admin/login');

        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
          const storedRefreshToken = localStorage.getItem(KEY_REFRESH_TOKEN);
          if (!storedRefreshToken) {
            _clearSession();
            return Promise.reject(error);
          }

          if (isRefreshing.current) {
            return new Promise((resolve, reject) => {
              refreshSubscribers.current.push({ resolve, reject });
            }).then((newToken) => {
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              return axios(originalRequest);
            });
          }

          originalRequest._retry  = true;
          isRefreshing.current    = true;

          try {
            const res = await axios.post(`${API_URL}/auth/refresh`, {
              refresh_token: storedRefreshToken,
            });
            const { token: newToken, refresh_token: newRefreshToken } = res.data;

            localStorage.setItem(KEY_TOKEN, newToken);
            localStorage.setItem(KEY_REFRESH_TOKEN, newRefreshToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            setToken(newToken);

            refreshSubscribers.current.forEach(({ resolve }) => resolve(newToken));
            refreshSubscribers.current = [];

            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            refreshSubscribers.current.forEach(({ reject }) => reject(refreshError));
            refreshSubscribers.current = [];
            _clearSession();
            return Promise.reject(refreshError);
          } finally {
            isRefreshing.current = false;
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Initialisation de session — OFFLINE-FIRST ───────────────────────────
  // Comportement similaire à Outlook :
  //   1. Restauration immédiate depuis le cache local (pas d'attente réseau)
  //   2. Vérification réseau en arrière-plan
  //   3. Session invalidée UNIQUEMENT sur 401 (token révoqué), jamais sur erreur réseau
  useEffect(() => {
    const initAuth = async () => {
      const savedToken    = localStorage.getItem(KEY_TOKEN);
      const cachedUserRaw = localStorage.getItem(KEY_USER_CACHE);

      if (savedToken) {
        // ── Étape 1 : restauration immédiate depuis le cache ──────────────
        axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
        setToken(savedToken);

        if (cachedUserRaw) {
          try {
            setUser(JSON.parse(cachedUserRaw)); // affiche l'app immédiatement
          } catch {
            // JSON corrompu — on attend la réponse réseau
          }
        }

        setLoading(false); // <-- L'interface s'affiche maintenant, offline ou non

        // ── Étape 2 : vérification réseau en arrière-plan ─────────────────
        try {
          const response = await axios.get(`${API_URL}/auth/me`);
          const freshUser = response.data;
          setUser(freshUser);
          // Mise à jour du cache avec les données fraîches
          localStorage.setItem(KEY_USER_CACHE, JSON.stringify(freshUser));
        } catch (error) {
          if (error.response?.status === 401) {
            // Token révoqué côté serveur (ex. changement de mot de passe)
            // L'intercepteur a déjà tenté le refresh — si on est ici, c'est définitivement invalide
            _clearSession();
          }
          // Toute autre erreur (réseau coupé, timeout…) : on garde la session locale
        }
      } else {
        setLoading(false);
      }
    };

    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Helpers privés ──────────────────────────────────────────────────────
  const _clearSession = () => {
    localStorage.removeItem(KEY_TOKEN);
    localStorage.removeItem(KEY_REFRESH_TOKEN);
    localStorage.removeItem(KEY_USER_EMAIL);
    localStorage.removeItem(KEY_USER_CACHE);
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  // ── API publique du contexte ────────────────────────────────────────────
  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { token: newToken, refresh_token: newRefreshToken, user: userData } = response.data;

    localStorage.setItem(KEY_TOKEN, newToken);
    localStorage.setItem(KEY_REFRESH_TOKEN, newRefreshToken);
    localStorage.setItem(KEY_USER_EMAIL, email);
    // Cache du profil pour le mode hors-ligne
    localStorage.setItem(KEY_USER_CACHE, JSON.stringify(userData));

    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);

    return userData;
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem(KEY_REFRESH_TOKEN);
    if (refreshToken) {
      try {
        await axios.post(`${API_URL}/auth/logout`, { refresh_token: refreshToken });
      } catch {
        // Nettoyage local quoi qu'il arrive
      }
    }
    _clearSession();
  };

  const verifySession = async () => {
    try {
      await axios.post(`${API_URL}/auth/verify-session`);
      return true;
    } catch {
      return false;
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      const freshUser = response.data;
      setUser(freshUser);
      localStorage.setItem(KEY_USER_CACHE, JSON.stringify(freshUser));
    } catch (error) {
      console.error('Impossible de rafraîchir le profil :', error);
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token && !!user,
    login,
    logout,
    verifySession,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
